/**
 * Theme utilities for KLASSIC
 * Handles theme initialization and application
 */

export type Theme = 'light' | 'dark' | 'system'

/**
 * Initialize theme on app startup
 * Should be called early in the app lifecycle
 */
export function initializeTheme(): void {
  const savedTheme = (localStorage.getItem('theme') as Theme | null) || 'system'
  applyTheme(savedTheme)
}

/**
 * Apply a theme to the document
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement
  root.classList.remove('light', 'dark')

  if (theme === 'system') {
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light'
    root.classList.add(systemTheme)
  } else {
    root.classList.add(theme)
  }
}

/**
 * Get the current saved theme
 */
export function getTheme(): Theme {
  return (localStorage.getItem('theme') as Theme | null) || 'system'
}

/**
 * Set and apply a theme
 */
export function setTheme(theme: Theme): void {
  localStorage.setItem('theme', theme)
  applyTheme(theme)
}

/**
 * Listen for system theme changes when using 'system' theme
 */
export function watchSystemTheme(): () => void {
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

  const handleChange = () => {
    const currentTheme = getTheme()
    if (currentTheme === 'system') {
      applyTheme('system')
    }
  }

  mediaQuery.addEventListener('change', handleChange)

  // Return cleanup function
  return () => {
    mediaQuery.removeEventListener('change', handleChange)
  }
}
